<template>
  <two-columns
    @resize="handleResize"
    :switch-to-first-button="$t('Show meals list')"
    :switch-to-second-button="$t('Show menu')"
  >
    <div slot="first-column-header">
      <div class="columns is-mobile">
        <div class="column is-four-fifth">
          <h1 class="title is-size-4-mobile">
            {{ $t("Food list") }} ({{ otherMeals.length }})
          </h1>
        </div>
        <div v-if="isAdmin" class="column is-one-fifth">
          <router-link
            class="button is-primary is-outlined is-pulled-right"
            :to="`/projects/${projectId}/meals/create`"
          >
            <span class="icon is-small"><i class="fa fa-plus"/></span>
            <span>{{ $t("Create") }}</span>
          </router-link>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <div class="is-flex">
            <input-field
              :isFullWidth="true"
              type="search"
              v-model="searchQuery"
              :placeholder="$t('Search meals...')"
            />
            <sort-button
              class="sort-button"
              :sorters="sorters"
              :activeSorter="sorter"
              @change="setSorter"
            />
          </div>
        </div>
      </div>
      <div class="columns" v-if="categories.length">
        <div class="column"><filter-buttons v-model="filterCategories" /></div>
      </div>
    </div>
    <draggable
      slot="first-column-body"
      :value="otherMeals"
      :options="{
        group: 'meals',
        draggable: '.meal-item',
        filter: '.button',
        sort: false,
        disabled: !isDesktop
      }"
      class="draggable-placeholder"
    >
      <meal-item
        v-for="meal in otherMeals"
        :key="meal.id"
        :meal="meal"
        :currency="currency"
        :highlight="searchQuery"
      >
        <div slot="right-bottom" class="buttons has-addons">
          <router-link
            class="button"
            :to="`/projects/${projectId}/meals/${meal.id}`"
            :title="$t('Edit')"
          >
            <span class="icon is-small"><i class="fa fa-edit"/></span>
          </router-link>
          <a
            class="button is-primary is-outlined is-pulled-right"
            @click="addToOffer(meal.id)"
            :title="$t('Add')"
          >
            <span class="icon is-small"><i class="fa fa-plus"/></span>
          </a>
        </div>
      </meal-item>
      <div v-if="Object.values(otherMeals).length == 0">
        {{ $t("List is empty") }}
      </div>
    </draggable>
    <div slot="second-column-header">
      <div class="columns is-mobile">
        <div class="column">
          <h1 class="title is-size-4-mobile">
            {{ menuName }} ({{ menuMeals.length }} / {{ maxMeals }})
          </h1>
        </div>
        <div v-if="isAdmin" class="column is-one-fifth">
          <router-link
            class="button is-primary is-outlined is-pulled-right"
            :to="`/projects/${projectId}/offers-management/${offerId}`"
          >
            <span class="icon is-small"><i class="fa fa-cog"/></span>
            <span>{{ $t("Offer settings") }}</span>
          </router-link>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <input-field
            :isFullWidth="true"
            type="search"
            v-model="searchQueryOffer"
            :placeholder="$t('Search meals...')"
          />
        </div>
      </div>
    </div>
    <draggable
      slot="second-column-body"
      v-model="menuMeals"
      :options="{
        group: 'meals',
        draggable: '.meal-item',
        filter: '.button',
        disabled: !isDesktop
      }"
      class="draggable-placeholder"
    >
      <meal-item
        v-for="(meal, index) in menuMeals"
        :key="meal.id"
        :meal="meal"
        :currency="currency"
        :highlight="searchQueryOffer"
      >
        <div slot="right-bottom" class="buttons has-addons">
          <router-link
            class="button"
            :to="`/projects/${projectId}/meals/${meal.id}`"
            :title="$t('Edit')"
          >
            <span class="icon is-small"><i class="fa fa-edit"/></span>
          </router-link>
          <a
            class="button is-outlined"
            @click="moveUpInOffer(menuMeals, index)"
            :disabled="index == 0"
            :title="$t('Move up')"
          >
            <span class="icon is-small"><i class="fa fa-arrow-up"/></span>
          </a>
          <a
            class="button is-outlined"
            @click="moveDownInOffer(menuMeals, index)"
            :disabled="index == menuMeals.length - 1"
            :title="$t('Move down')"
          >
            <span class="icon is-small"><i class="fa fa-arrow-down"/></span>
          </a>
          <a
            class="button is-outlined is-danger"
            @click="removeFromOffer(meal.id)"
            :title="$t('Remove')"
          >
            <span class="icon is-small"><i class="fa fa-minus"/></span>
          </a>
        </div>
        <button slot="footer">Add</button>
      </meal-item>
      <div v-if="Object.values(menuMeals).length == 0">
        {{ $t("Add meal here") }}
      </div>
    </draggable>
  </two-columns>
</template>

<script>
import { state, actions } from "@/store"
import { arraySwap } from "@/utils"
import MealItem from "@/components/meal-item"
import SortButton from "@/components/sort-button"
import FilterButtons from "@/components/form/filter-buttons"
import { TwoColumns } from "@/components/layout"
import draggable from "vuedraggable"
import mealService from "@/service/meal-service"
import { InputField } from "@/components/form"

export default {
  name: "offer",
  props: ["projectId", "offerId"],
  components: {
    TwoColumns,
    draggable,
    MealItem,
    SortButton,
    FilterButtons,
    InputField
  },
  data() {
    return {
      searchQuery: undefined,
      searchQueryOffer: undefined,
      isDesktop: false,
      filterCategories: [],
      sorters: [
        {
          sortBy: "name",
          text: "Name (alphabetical)",
          compareFn: mealService.compareByNameAsc,
          icon: "fa-sort-alpha-asc"
        },
        {
          sortBy: "date",
          text: "Date update (from newest)",
          compareFn: mealService.compareByDateDesc,
          icon: "fa-sort-numeric-desc"
        }
      ]
    }
  },
  watch: {
    categories: {
      immediate: true,
      handler(categories) {
        this.filterCategories = categories.map(category => ({
          value: category,
          selected: false
        }))
      }
    }
  },
  computed: {
    currency() {
      return state.project.data.currency
    },
    menuMeals: {
      get() {
        return actions
          .getSortedOfferMeals(this.offerId)
          .filter(mealService.filterPredicate(this.searchQueryOffer))
      },
      set(offerMeals) {
        actions.clearErrors()
        actions
          .saveMealIdsToOffer(offerMeals, this.offerId)
          .catch(actions.addError("save-offer-error"))
      }
    },
    otherMeals() {
      const offer = state.project.data.offers[this.offerId]
      return Object.values(state.project.data.meals)
        .filter(meal => offer && !offer.data.mealIds[meal.id])
        .filter(mealService.filterPredicate(this.searchQuery))
        .filter(mealService.filterCategoriesPredicate(this.filterCategories))
        .sort(
          mealService.unionComparator(
            mealService.compareByCategoriesCountDesc(this.filterCategories),
            this.sorter.compareFn
          )
        )
    },
    menuName() {
      return (
        state.project.data.offers[this.offerId] &&
        state.project.data.offers[this.offerId].conf.name
      )
    },
    isAdmin() {
      return state.isAdmin
    },
    sorter() {
      const sortBy =
        state.project.members[state.user.email] &&
        state.project.members[state.user.email].mealsSortBy
      return (
        this.sorters.find(sorter => sorter.sortBy === sortBy) || this.sorters[0]
      )
    },
    categories() {
      return state.project.data.availableCategories
    },
    maxMeals() {
      const offer = state.project.data.offers[this.offerId]
      if (offer == null) {
        return
      }
      const templateId = offer.conf.templateId
      const screens = Object.keys(offer.conf.screens).length
      const template = state.templates[templateId]
      return screens * template.maxMeals
    }
  },
  methods: {
    addToOffer(mealId) {
      actions.clearErrors()
      actions
        .addMealIdToOffer(mealId, this.offerId)
        .catch(actions.addError("add-to-offer-error"))
    },
    removeFromOffer(mealId) {
      actions.clearErrors()
      actions
        .removeMealIdFromOffer(mealId, this.offerId)
        .catch(actions.addError("remove-from-offer-error"))
    },
    moveUpInOffer(meals, index) {
      if (index > 0) {
        this.menuMeals = arraySwap(meals, index, index - 1)
      }
    },
    moveDownInOffer(meals, index) {
      if (index < meals.length - 1) {
        this.menuMeals = arraySwap(meals, index, index + 1)
      }
    },
    handleResize(isDesktop) {
      this.isDesktop = isDesktop
    },
    setSorter(sorter) {
      actions.clearErrors()
      actions
        .setMealsSortByToMember(sorter.sortBy)
        .catch(actions.addError("save-meals-sortby-error"))
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/style.scss";

.draggable-placeholder {
  min-height: calc(100% - 16px);
}
.sortable-ghost {
  background-color: $primary;
  border: 1px solid black;
}
.sortable-chosen {
  background-color: $primary;
}
label.is-normal {
  padding-top: 0.375em;
  margin-right: 0.5em;
}
.sort-button {
  margin-left: 0.3rem;
}
.columns {
  margin-bottom: 0;
}
</style>
